import { t } from "i18next"
import React, { createContext, useEffect, useRef, useState } from "react"
import { client } from "../../.."
import { GetBidsForOrgId } from "../../../api/api"
import { BrandLoader } from "../../../components/BrandLoader"
import { MissingPermissionView } from "../../../components/MissingPermissionView"
import { OrgBidModel } from "../../../models/bid/OrgBidModel"
import { NiagaMarket } from "../../../models/niaga-market/NiagaMarket"
import { PRICE_CODE_CAN_PUBLISH_PRICE_DEMAND, PRICE_CODE_CAN_PUBLISH_PRICE_NOT_DEMAND, PRICE_CODE_MARKET_CLOSE, PRICE_CODE_NO_ORG_LOCATION, OrganisationWithPermission } from "../MyPrices"
import { ExpandablePrice } from "./ExpandablePrice"

type Props = {
    orgParam: OrganisationWithPermission
    setSelectedOrg?: (org: OrgBidModel) => void
    onPriceUpdated?: () => void
}

export type PriceReloadContextValue = { reload: () => void }
export const PriceReloadContext = createContext<PriceReloadContextValue>({ reload: () => { } })

export const PriceContainer = ({ orgParam, setSelectedOrg, onPriceUpdated }: Props) => {

    const [openCharts, setOpenCharts] = useState(false)

    const [bids, setBids] = useState<OrgBidModel | undefined>(undefined)

    // The org can legitimately have no bid row, so we track the fetch separately from its result
    const [loaded, setLoaded] = useState(false)

    const [loading, setLoading] = useState(false)

    useEffect(() => {
        return () => { setBids(undefined); setLoaded(false) }
    }, [])

    const reload = () => {
        setLoading(true)
        client.query({
            query: GetBidsForOrgId,
            variables: {
                id: orgParam.organisation.id
            },
            context: {
                headers: {
                    "role": "usermill"
                }
            }
        }).then(data => {
            setBids(data.data.bids?.at(0) ? OrgBidModel.buildOrgBid(data.data.bids[0]) : undefined)
            setLoaded(true)
            onPriceUpdated()
        }).finally(() => setLoading(false))
    }


    const onPriceClick = (org: OrgBidModel) => {
        setSelectedOrg?.(org)
    }

    const canEditPrice = (org: OrgBidModel) => {
        if (!org.hasPosition() || !orgParam.permissions.canWriteBid()) {
            return false
        }
        return true
    }

    function priceStatusCode(org: OrgBidModel): number {
        if (!org?.hasPosition()) {
            return PRICE_CODE_NO_ORG_LOCATION
        } else {
            return PRICE_CODE_CAN_PUBLISH_PRICE_DEMAND
        }
    }

    const errorReason = (org: OrgBidModel) => {
        if (!org.hasPosition()) {
            return t('need_mill_position')
        }
        return null
    }

    useEffect(() => {
        reload()
    }, [orgParam])


    return (
        loaded ?
            <PriceReloadContext.Provider value={{ reload: reload }}>
                <div className="pempem-flex-col-center gap10" key={orgParam.organisation.id}>
                    {
                        bids?.organisationId &&
                        <ExpandablePrice onPriceClick={onPriceClick}
                            key={bids.organisationId}
                            bids={bids}
                            isEditable={canEditPrice}
                            getErrorReason={errorReason}
                            getPriceStatus={priceStatusCode} />
                    }
                    <MissingPermissionView context="bid" title={t("missing_permission")} orgPermission={orgParam.permissions} />
                </div>
            </PriceReloadContext.Provider> : <BrandLoader isLoading={loading} />
    )
}